/**
 * Billing / checkout API layer.
 *
 * Reads the tier catalog from the DB (community_tiers = K&C, alfie_tiers = Alfie
 * SaaS) and talks to the `stripe-checkout` edge function. The two product lines
 * are independent: a member can pick a K&C tier and/or an Alfie tier.
 */
import { supabase } from '@/lib/supabase';

export type BillingInterval = 'monthly' | 'annual';

/**
 * Who owns an `organization_alfie_subscriptions` row: `stripe` = a paid,
 * webhook-provisioned plan; `addon` = a plan INCLUDED via an active add-on grant
 * (`managed_by='addon'`, no Stripe charge behind it — contract 3.4iii); `null` =
 * legacy/unknown provenance (treated as owned). A Stripe row and an addon row can
 * be active at the same time, so readers that show "the current plan" must both
 * consider the highest across the two lanes AND label the addon lane.
 */
export type AlfieManagedBy = 'stripe' | 'addon' | null;

/** Alfie plan ladder rank (higher = more capable). Unknown codes sort below FREE. */
const ALFIE_PLAN_RANK: Record<string, number> = { FREE: 0, PRO: 1, BUSINESS: 2, ENTERPRISE: 3 };

function alfiePlanRank(code: string | null | undefined): number {
  if (!code) return -1;
  return ALFIE_PLAN_RANK[code] ?? 0;
}

/** Normalise the raw `managed_by` column to the AlfieManagedBy union. */
function toManagedBy(raw: unknown): AlfieManagedBy {
  return raw === 'addon' ? 'addon' : raw === 'stripe' ? 'stripe' : null;
}

export interface KcTierOption {
  code: string;
  name: string;
  priceMonthlyCents: number | null;
  /** The Alfie plan this product bundles (an alfie_tiers.code), or null. */
  includedAlfieTierCode: string | null;
  /** A Stripe price is configured → self-serve checkout can complete. */
  sellable: boolean;
}

export interface AlfieTierOption {
  code: string;
  name: string;
  /** One-line description (Enterprise: the brochure line, migration 672). */
  description?: string | null;
  seats: number;
  aiInteractionsPerMonth: number | null;
  priceMonthlyCents: number | null;
  priceAnnualCents: number | null;
  requiresAgreement: boolean;
  /** Tier includes the Basecamp course (migration 334). */
  includesBasecamp: boolean;
  /** A monthly Stripe price is configured → self-serve checkout can complete. */
  sellable: boolean;
}

export interface Catalog {
  kc: KcTierOption[];
  alfie: AlfieTierOption[];
}

// Every K&C product /checkout can RENDER, in ladder order. Each bundles an
// Alfie plan (included_alfie_tier_code). Buyability is per-product in the UI:
// WunderPack/Vault are self-serve; K2K/Inner Circle are by application;
// Founders Table is invitation-only. ADMIN is internal — excluded.
// BASECAMP renders as a marketing-skin card whose CTA subscribes to Alfie PRO
// (KC_ALIAS_TO_ALFIE) — the BASECAMP tier itself is retired from sale and the
// backend rejects a direct kc:BASECAMP purchase.
const KC_DISPLAY_CODES = [
  'BASECAMP',
  'WUNDERPACK',
  'VAULT',
  'K2K',
  'INNER_CIRCLE',
  'FOUNDERS_TABLE',
];

export async function fetchCatalog(): Promise<Catalog> {
  const [kcRes, alfieRes] = await Promise.all([
    supabase
      .from('community_tiers')
      .select(
        'code, name, price_monthly_cents, stripe_price_id, included_alfie_tier_code, tier_level',
      )
      .in('code', KC_DISPLAY_CODES)
      .order('tier_level', { ascending: true }),
    supabase
      .from('alfie_tiers')
      .select(
        'code, name, description, seats, ai_interactions_per_month, price_monthly_cents, price_annual_cents, stripe_price_id_monthly, requires_agreement, includes_basecamp, is_active, display_order',
      )
      .eq('is_active', true)
      .order('display_order', { ascending: true }),
  ]);

  if (kcRes.error) throw new Error(`Failed to load K&C tiers: ${kcRes.error.message}`);
  if (alfieRes.error) throw new Error(`Failed to load Alfie tiers: ${alfieRes.error.message}`);

  const kc: KcTierOption[] = ((kcRes.data ?? []) as Array<{
    code: string;
    name: string;
    price_monthly_cents: number | null;
    stripe_price_id: string | null;
    included_alfie_tier_code: string | null;
  }>).map((r) => ({
    code: r.code,
    name: r.name,
    priceMonthlyCents: r.price_monthly_cents,
    includedAlfieTierCode: r.included_alfie_tier_code ?? null,
    sellable: !!r.stripe_price_id,
  }));

  const alfie: AlfieTierOption[] = ((alfieRes.data ?? []) as Array<{
    code: string;
    name: string;
    description: string | null;
    seats: number;
    ai_interactions_per_month: number | null;
    price_monthly_cents: number | null;
    price_annual_cents: number | null;
    stripe_price_id_monthly: string | null;
    requires_agreement: boolean;
    includes_basecamp: boolean | null;
  }>).map((r) => ({
    code: r.code,
    name: r.name,
    description: r.description ?? null,
    seats: r.seats,
    aiInteractionsPerMonth: r.ai_interactions_per_month,
    priceMonthlyCents: r.price_monthly_cents,
    priceAnnualCents: r.price_annual_cents,
    requiresAgreement: r.requires_agreement,
    includesBasecamp: r.includes_basecamp ?? false,
    sellable: !!r.stripe_price_id_monthly,
  }));

  return { kc, alfie };
}

// ── Checkout selections + edge-function payloads ──────────────────────────────

export interface CheckoutSelections {
  alfieTier?: { code: string; interval: BillingInterval };
  kcTier?: { code: string };
}

export interface CheckoutLineItem {
  kind: 'kc' | 'alfie';
  code: string;
  name: string;
  interval: BillingInterval;
  amountCents: number | null;
}

interface InitFreeResponse {
  free: true;
}
interface InitPaidResponse {
  clientSecret: string;
  customerId: string;
  subscriptionId: string;
  lineItems: CheckoutLineItem[];
  totalCents: number;
  /** Sales in Alfie only: 'setup' when a 100%-off first invoice needs just the card. Absent → 'payment'. */
  mode?: 'payment' | 'setup';
  /** Sales in Alfie only: the sale's promo code, applied to totalCents. */
  promoCode?: string | null;
}
/** The org already actively holds this product — no payment needed. */
interface InitAlreadyActiveResponse {
  alreadyActive: true;
}
export type InitResponse = InitFreeResponse | InitPaidResponse | InitAlreadyActiveResponse;

export function isFreeResponse(r: InitResponse): r is InitFreeResponse {
  return (r as InitFreeResponse).free === true;
}

export function isAlreadyActiveResponse(r: InitResponse): r is InitAlreadyActiveResponse {
  return (r as InitAlreadyActiveResponse).alreadyActive === true;
}

/**
 * The error CODE an edge function returned, e.g. 'K2K_NOT_APPROVED'.
 *
 * supabase-js throws FunctionsHttpError whose `.context` is the raw `Response`
 * — NOT a parsed body — so the body has to be read to see `{ error }`. Reading
 * `.context.error` directly (as this file used to) always yielded undefined and
 * collapsed every distinct code into the generic
 * "Edge Function returned a non-2xx status code", which then got rendered
 * verbatim to buyers. The Response is unread at this point (the client throws
 * before consuming it) and cloned anyway, so this is always safe.
 */
async function edgeErrorMessage(error: unknown, fallback: string): Promise<string> {
  const ctx = (error as { context?: unknown }).context;
  if (typeof Response !== 'undefined' && ctx instanceof Response) {
    try {
      const body = (await ctx.clone().json()) as { error?: unknown } | null;
      if (typeof body?.error === 'string' && body.error) return body.error;
    } catch {
      // Non-JSON body (gateway HTML, empty 500) — fall through.
    }
  } else if (typeof (ctx as { error?: unknown } | null)?.error === 'string') {
    // Defensive: other/older client shapes that do expose a parsed body.
    return (ctx as { error: string }).error;
  }
  return (error as { message?: string } | null)?.message || fallback;
}

async function invokeCheckout<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke<T>('stripe-checkout', {
    body,
  });
  if (error) {
    // Edge function returns { error } with a non-2xx; surface its code.
    throw new Error(await edgeErrorMessage(error, 'Checkout request failed'));
  }
  if (!data) throw new Error('Empty checkout response');
  return data;
}

/**
 * Create the subscription (incomplete) and return its first invoice's PaymentIntent
 * client_secret, which the frontend confirms on-session. A FREE-only cart provisions
 * directly (returns { free: true }); an already-held product returns { alreadyActive }.
 */
export function initCheckout(
  selections: CheckoutSelections,
  opts: { saleCode?: string } = {},
): Promise<InitResponse> {
  return invokeCheckout<InitResponse>({
    action: 'init',
    selections,
    ...(opts.saleCode ? { saleCode: opts.saleCode } : {}),
  });
}

/** stripe-checkout refused the sale link (wrong account or plan, used, or run out). */
export const SALE_LINK_NOT_VALID_ERROR = 'SALE_LINK_NOT_VALID';
/** The sale's promo code no longer works in Stripe. */
export const SALE_PROMO_NOT_VALID_ERROR = 'SALE_PROMO_NOT_VALID';

// ── Post-payment provisioning (success-screen polling) ────────────────────────

/**
 * Which of the given Stripe subscription ids the webhook has provisioned: an
 * active row keyed by that id in either entitlement table.
 */
export async function fetchProvisionedSubscriptionIds(
  subscriptionIds: string[],
): Promise<string[]> {
  if (subscriptionIds.length === 0) return [];
  const [kc, alfie] = await Promise.all([
    supabase
      .from('user_community_memberships')
      .select('stripe_subscription_id')
      .in('stripe_subscription_id', subscriptionIds)
      .in('status', ['active']),
    supabase
      .from('organization_alfie_subscriptions')
      .select('stripe_subscription_id')
      .in('stripe_subscription_id', subscriptionIds)
      .in('status', ['active']),
  ]);
  if (kc.error) throw new Error(kc.error.message);
  if (alfie.error) throw new Error(alfie.error.message);

  const ids = [...(kc.data ?? []), ...(alfie.data ?? [])]
    .map((r) => (r as { stripe_subscription_id: string | null }).stripe_subscription_id)
    .filter((id): id is string => !!id);
  return [...new Set(ids)];
}

/**
 * Poll until every subscription id is provisioned by the webhook, or attempts
 * run out. Transient read failures don't abort the poll. Defaults to ~30s.
 */
export async function waitForProvisioning(
  subscriptionIds: string[],
  { attempts = 20, intervalMs = 1500 }: { attempts?: number; intervalMs?: number } = {},
): Promise<boolean> {
  for (let i = 0; i < attempts; i++) {
    try {
      const done = await fetchProvisionedSubscriptionIds(subscriptionIds);
      if (subscriptionIds.every((id) => done.includes(id))) return true;
    } catch {
      // transient read failure: keep polling
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return false;
}

// ── Current subscriptions (preselection on /checkout) ─────────────────────────

export interface CurrentSubscriptions {
  /**
   * The org's effective current Alfie plan for DISPLAY: the highest-ranked
   * active/past_due row across BOTH lanes. A paid Stripe row and an
   * addon-managed (included) row can coexist (contract 3.4iii), so this is the
   * higher of the two — a Stripe row wins ties. `managedBy='addon'` means the
   * plan is INCLUDED via an add-on grant, not owned: the UI badges it "Included
   * via add-on" and keeps the purchase path open.
   */
  alfie: { code: string; interval: BillingInterval; managedBy: AlfieManagedBy } | null;
  /**
   * The signed-in user's OWN active K&C seat. Access is per-seat
   * (get_user_tier reads the caller's seat), so this — not the org's
   * highest seat — is what "current plan" means on the checkout page.
   */
  kc: { code: string } | null;
  /** True when the org holds ANY active K&C seat — locks new K&C purchases. */
  orgHasKcSeat: boolean;
}

/**
 * Order K&C seats so the org's representative plan is first: highest `tier_level`
 * wins; a Stripe-backed seat beats a comp seat on ties. The SAME rule is used by
 * the Org tab (`fetchOrgSubscriptionStatuses`), Billing (`stripe-billing.activeKcSub`),
 * and Checkout (`fetchCurrentSubscriptions`) so the three never disagree on a
 * multi-seat org (a per-seat org can hold several active seats at different tiers).
 */
export function byPrimaryKcSeat(
  a: { stripe_subscription_id?: string | null; community_tiers: { tier_level: number } | null },
  b: { stripe_subscription_id?: string | null; community_tiers: { tier_level: number } | null },
): number {
  const at = a.community_tiers?.tier_level ?? -1;
  const bt = b.community_tiers?.tier_level ?? -1;
  if (bt !== at) return bt - at;
  return (b.stripe_subscription_id ? 1 : 0) - (a.stripe_subscription_id ? 1 : 0);
}

/**
 * The active subscriptions the checkout page renders against: the org's Alfie
 * plan, plus the signed-in USER's own K&C seat (`kc`) and whether the org holds
 * any active seat at all (`orgHasKcSeat`, the purchase lock). A multi-seat org
 * can hold seats at several tiers — marking the org's HIGHEST seat as the
 * viewer's "current plan" (the old behaviour) misstated their membership, since
 * access is per-seat. Org-level surfaces (Billing, Org tab) keep the
 * byPrimaryKcSeat representative-plan rule; this one is viewer-scoped.
 * RLS: the Alfie row is readable by any active org member; K&C seat rows by
 * their holder (and org admins). A member always sees at least their own seat.
 */
export async function fetchCurrentSubscriptions(
  orgId: string,
  userId: string,
): Promise<CurrentSubscriptions> {
  const [alfieRes, kcRes] = await Promise.all([
    // ALL active/past_due rows, not just the newest: a paid Stripe row and an
    // addon-managed (included) row can coexist, so pick the HIGHEST across both
    // lanes rather than whichever was created last. past_due included (hermes
    // 480): a sub in dunning is still the org's subscription.
    supabase
      .from('organization_alfie_subscriptions')
      .select('billing_interval, managed_by, alfie_tiers(code)')
      .eq('organization_id', orgId)
      .in('status', ['active', 'past_due']),
    // Per-seat (352): read all the org's granting seats in one query — the
    // viewer's own seat drives `kc`, the set as a whole drives `orgHasKcSeat`.
    supabase
      .from('user_community_memberships')
      .select('user_id, stripe_subscription_id, community_tiers!inner(code, tier_level)')
      .eq('organization_id', orgId)
      .in('status', ['active', 'past_due']),
  ]);

  const alfieRows = (alfieRes.data ?? []) as unknown as Array<{
    billing_interval: string | null;
    managed_by: string | null;
    alfie_tiers: { code: string } | null;
  }>;
  // Highest plan across both lanes; a Stripe/owned row beats an addon row on ties
  // (so an owned plan is shown as owned, not "included").
  const alfieRow = alfieRows
    .filter((r) => r.alfie_tiers?.code)
    .sort((a, b) => {
      const rank = alfiePlanRank(b.alfie_tiers?.code) - alfiePlanRank(a.alfie_tiers?.code);
      if (rank !== 0) return rank;
      return (a.managed_by === 'addon' ? 1 : 0) - (b.managed_by === 'addon' ? 1 : 0);
    })[0] ?? null;
  const kcRows = (kcRes.data ?? []) as unknown as Array<{
    user_id: string | null;
    stripe_subscription_id: string | null;
    community_tiers: { code: string; tier_level: number } | null;
  }>;
  const ownSeat = kcRows
    .filter((r) => r.user_id === userId)
    .sort(byPrimaryKcSeat)[0] ?? null;

  return {
    alfie: alfieRow?.alfie_tiers?.code
      ? {
        code: alfieRow.alfie_tiers.code,
        interval: alfieRow.billing_interval === 'annual' ? 'annual' : 'monthly',
        managedBy: toManagedBy(alfieRow.managed_by),
      }
      : null,
    kc: ownSeat?.community_tiers?.code ? { code: ownSeat.community_tiers.code } : null,
    orgHasKcSeat: kcRows.some((r) => r.community_tiers != null),
  };
}

// ── Subscription status display (Settings → Organisation) ─────────────────────

export interface AlfieSubscriptionStatus {
  tierName: string | null;
  status: string;
  billingInterval: BillingInterval;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  /**
   * `addon` = this plan is INCLUDED via an add-on grant (no Stripe charge, no
   * cancel/portal). The Organisation summary labels it "included via add-on"
   * rather than presenting it as a paid subscription.
   */
  managedBy: AlfieManagedBy;
}

export interface KcMembershipStatus {
  tierName: string | null;
  status: string;
}

export interface OrgSubscriptionStatuses {
  alfie: AlfieSubscriptionStatus | null;
  kc: KcMembershipStatus | null;
}

// An org can carry history (e.g. a cancelled sub plus a new one) — surface the
// live row over dead ones.
const STATUS_PRECEDENCE = ['active', 'past_due', 'suspended', 'expired', 'cancelled'];

function pickCurrent<T extends { status: string }>(rows: T[]): T | null {
  for (const status of STATUS_PRECEDENCE) {
    const row = rows.find((r) => r.status === status);
    if (row) return row;
  }
  return rows[0] ?? null;
}

/**
 * Status of both product-line subscriptions for the org (Alfie SaaS + K&C
 * membership), regardless of status — unlike `fetchCurrentSubscriptions`, a
 * past_due/cancelled sub is still returned so Settings can show its state.
 * Same RLS caveat: non-admins can't read the org K&C row and get `kc: null`.
 */
export async function fetchOrgSubscriptionStatuses(
  orgId: string,
): Promise<OrgSubscriptionStatuses> {
  const [alfieRes, kcRes] = await Promise.all([
    supabase
      .from('organization_alfie_subscriptions')
      .select(
        'status, billing_interval, current_period_end, cancel_at_period_end, managed_by, alfie_tiers(name, code)',
      )
      .eq('organization_id', orgId)
      .order('created_at', { ascending: false }),
    // Per-seat (352): the org's K&C is its seat rows (user_id set). Read the org's
    // seats (org admins/owners can read org rows via RLS) so this matches the
    // service-role Billing tab. We sort by byPrimaryKcSeat (highest tier) before
    // pickCurrent so the representative tier matches Billing + Checkout exactly.
    supabase
      .from('user_community_memberships')
      .select('status, stripe_subscription_id, community_tiers!inner(name, tier_level)')
      .eq('organization_id', orgId),
  ]);

  if (alfieRes.error) {
    throw new Error(`Failed to load Alfie subscription: ${alfieRes.error.message}`);
  }
  if (kcRes.error) throw new Error(`Failed to load K&C membership: ${kcRes.error.message}`);

  // The FK joins are many-to-one: PostgREST returns an object, but without
  // generated DB types supabase-js infers an array — hence the unknown cast.
  //
  // Pick the row that best represents the org's plan for the Billing tab, within
  // the winning status (pickCurrent applies status precedence):
  //   0. a real PAID owned row (Stripe/legacy, non-FREE): its period-end/cancel
  //      describe the actual subscription, and it beats an addon even when the
  //      addon plan ranks higher (contract 3.4iii — show the stripe plan);
  //   1. otherwise an addon-included row: surfaced (and labelled "included via
  //      add-on") when there is no real paid plan, e.g. a FREE org holding a
  //      BUSINESS add-on bundle;
  //   2. otherwise the FREE/placeholder owned row.
  // Every org carries a FREE owned row from signup, so an addon is never truly
  // "lone" — without this ordering the included plan could never show.
  type AlfieStatusRow = {
    status: string;
    billing_interval: string | null;
    current_period_end: string | null;
    cancel_at_period_end: boolean;
    managed_by: string | null;
    alfie_tiers: { name: string; code: string } | null;
  };
  const displayPriority = (r: AlfieStatusRow): number => {
    if (r.managed_by === 'addon') return 1;
    return alfiePlanRank(r.alfie_tiers?.code) > 0 ? 0 : 2; // non-FREE owned first, FREE owned last
  };
  const alfieRowsSorted = ((alfieRes.data ?? []) as unknown as AlfieStatusRow[])
    .slice()
    .sort((a, b) => {
      const grp = displayPriority(a) - displayPriority(b);
      if (grp !== 0) return grp;
      // Within a group the higher-ranked plan represents the org.
      return alfiePlanRank(b.alfie_tiers?.code) - alfiePlanRank(a.alfie_tiers?.code);
    });
  const alfieRow = pickCurrent(alfieRowsSorted);
  const kcRows = (kcRes.data ?? []) as unknown as Array<{
    status: string;
    stripe_subscription_id: string | null;
    community_tiers: { name: string; tier_level: number } | null;
  }>;
  kcRows.sort(byPrimaryKcSeat);
  const kcRow = pickCurrent(kcRows);

  return {
    alfie: alfieRow
      ? {
        tierName: alfieRow.alfie_tiers?.name ?? null,
        status: alfieRow.status,
        billingInterval: alfieRow.billing_interval === 'annual' ? 'annual' : 'monthly',
        currentPeriodEnd: alfieRow.current_period_end,
        cancelAtPeriodEnd: alfieRow.cancel_at_period_end,
        managedBy: toManagedBy(alfieRow.managed_by),
      }
      : null,
    kc: kcRow ? { tierName: kcRow.community_tiers?.name ?? null, status: kcRow.status } : null,
  };
}

// ── Seat policy (Settings → Organisation, team management) ────────────────────

export interface OrgSeatPolicy {
  /** alfie_tiers.code governing the cap: FREE | PRO | BUSINESS | ENTERPRISE. */
  tierCode: string;
  /** Plan display name (e.g. "Business", "Pro", "Free"). */
  tierName: string;
  /** Member seat allowance for the plan (Business = 20, Pro/Free = 1). */
  seats: number;
}

/**
 * The org's member seat allowance, derived from its active Alfie plan. No
 * active plan → Free (1 seat). Readable by any active org member: the Alfie
 * subscription row is member-readable and the tier catalog is public, so every
 * member sees the cap (the backend stays the authority on enforcement).
 */
export async function fetchOrgSeatPolicy(orgId: string): Promise<OrgSeatPolicy> {
  // ALL active rows, not just the newest: an addon-included BUSINESS row
  // (mig 518) and a Stripe row can coexist, so the seat cap follows the
  // HIGHEST-seat plan across both lanes rather than whichever was created last.
  const { data, error } = await supabase
    .from('organization_alfie_subscriptions')
    .select('alfie_tiers ( code, name, seats )')
    .eq('organization_id', orgId)
    .in('status', ['active']);

  if (error) throw new Error(`Failed to load seat policy: ${error.message}`);

  const tiers = ((data ?? []) as unknown as Array<{
    alfie_tiers: { code: string; name: string; seats: number } | null;
  }>)
    .map((r) => r.alfie_tiers)
    .filter((t): t is { code: string; name: string; seats: number } => !!t?.seats);
  const tier = tiers.sort((a, b) => b.seats - a.seats)[0] ?? null;

  if (tier?.seats) {
    return { tierCode: tier.code, tierName: tier.name, seats: tier.seats };
  }
  // No active plan → Free allowance (the backend reads the live FREE row).
  return { tierCode: 'FREE', tierName: 'Free', seats: 1 };
}

/** Poll the member's active subscriptions to detect post-payment provisioning. */
export async function fetchActiveSubscriptionCount(orgId: string): Promise<number> {
  const [kc, alfie] = await Promise.all([
    supabase
      .from('user_community_memberships')
      .select('id', { count: 'exact', head: true })
      .eq('organization_id', orgId)
      .eq('status', 'active'),
    supabase
      .from('organization_alfie_subscriptions')
      .select('id', { count: 'exact', head: true })
      .eq('organization_id', orgId)
      .eq('status', 'active'),
  ]);
  return (kc.count ?? 0) + (alfie.count ?? 0);
}

export function formatAud(cents: number | null | undefined): string {
  if (cents == null) return '—';
  // Drop the .00 on whole-dollar amounts (all plan prices are whole dollars);
  // keep cents only when there's an actual fractional part.
  const hasCents = cents % 100 !== 0;
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: hasCents ? 2 : 0,
  }).format(cents / 100);
}

// ── Signup-time checkout (pre-email-confirmation) ──────────────────────────────
//
// Backs pages/SignupPage.tsx. Talks to the PUBLIC `stripe-checkout-signup` edge
// function (verify_jwt=false) — the caller has a freshly-created auth.users row
// but NO session yet (email confirmation is required before Supabase issues
// one), so every call here is unauthenticated: userId + email travel in the
// request body instead of riding a bearer token. supabase.functions.invoke
// attaches the anon apikey automatically even with no session.

export interface SignupAlfiePlan {
  code: string;
  name: string;
  priceMonthlyCents: number | null;
  seats: number;
  includesBasecamp: boolean;
}

export interface SignupKcTier {
  code: string;
  name: string;
  priceMonthlyCents: number | null;
  tierLevel: number;
  includedAlfieTierCode: string | null;
  /** A Stripe price is configured → self-serve checkout can complete. */
  purchasable: boolean;
}

export interface SignupCatalog {
  /** Excludes FREE and ENTERPRISE — neither is a paid Stripe-backed selection. */
  alfiePlans: SignupAlfiePlan[];
  kcTiers: SignupKcTier[];
}

export interface SignupCheckoutSelection {
  kind: 'alfie' | 'kc';
  code: string;
}

/** An applied promotion code, echoed back for the order summary. */
export interface SignupCheckoutDiscount {
  code: string;
  percentOff: number | null;
  amountOffCents: number | null;
}

interface SignupInitPaidResponse {
  clientSecret: string;
  /**
   * 'payment' → confirmPayment against the first invoice's PaymentIntent.
   * 'setup' → the first invoice was $0 (100%-off promo): the subscription is
   * already active and the secret belongs to a SetupIntent — confirmSetup
   * collects + saves the card for later invoices instead.
   * Optional because a not-yet-redeployed backend omits it; absent means
   * 'payment' (the only mode that existed before promo support).
   */
  mode?: 'payment' | 'setup';
  customerId: string;
  subscriptionId: string;
  tier: { kind: 'alfie' | 'kc'; code: string; name: string; priceMonthlyCents: number | null };
  /** First-invoice total after any discount, for "due today" display. */
  amountDueTodayCents?: number | null;
  discount?: SignupCheckoutDiscount | null;
}
/** The email is already entitled/active — no payment needed. */
interface SignupInitAlreadyActiveResponse {
  alreadyActive: true;
}
export type SignupInitResponse = SignupInitPaidResponse | SignupInitAlreadyActiveResponse;

export function isSignupAlreadyActiveResponse(
  r: SignupInitResponse,
): r is SignupInitAlreadyActiveResponse {
  return (r as SignupInitAlreadyActiveResponse).alreadyActive === true;
}

/** The generic-failure error code `init` throws as its message — see initSignupCheckout. */
export const SIGNUP_CHECKOUT_UNAVAILABLE = 'SIGNUP_CHECKOUT_UNAVAILABLE';

/**
 * Distinct init failure for an application-gated tier (K2K / Inner Circle): the
 * buyer's email has no approval yet. Thrown as the Error message by both
 * initSignupCheckout and the in-app initCheckout.
 *
 * `APPLICATION_NOT_APPROVED` is the current, tier-agnostic code (WS2);
 * `K2K_NOT_APPROVED` is the legacy code some deployments still emit for K2K —
 * callers must treat the two the same.
 */
export const APPLICATION_NOT_APPROVED = 'APPLICATION_NOT_APPROVED';
export const K2K_NOT_APPROVED = 'K2K_NOT_APPROVED';

/** True for either the current or the legacy "not approved for this tier" code. */
export function isApplicationNotApprovedError(message: string): boolean {
  return message === APPLICATION_NOT_APPROVED || message === K2K_NOT_APPROVED;
}

/**
 * Distinct init failure: the supplied promotion code doesn't resolve to an
 * active Stripe promotion code. Surfaced inline at the promo input — never a
 * flow-ending error.
 */
export const PROMO_CODE_INVALID = 'PROMO_CODE_INVALID';

async function invokeSignupCheckout<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke<T>('stripe-checkout-signup', {
    body,
  });
  if (error) {
    // Edge function returns { error } with a non-2xx; surface its message/code
    // (e.g. K2K_NOT_APPROVED, which SignupPage maps to specific copy).
    throw new Error(await edgeErrorMessage(error, 'Signup checkout request failed'));
  }
  if (!data) throw new Error('Empty signup checkout response');
  return data;
}

/** Alfie plans (excl. FREE/ENTERPRISE) + the K&C ladder, for the signup plan selector. */
export function fetchSignupCatalog(): Promise<SignupCatalog> {
  return invokeSignupCheckout<SignupCatalog>({ action: 'catalog' });
}

/**
 * Whether this email holds a sales approval to BUY an application-gated tier
 * (K2K today — sales tags the GHL contact 'k2k application approved' and the
 * webhook lands it on allowed_signups). Identity-less by design: the signup
 * page asks BEFORE the account exists, at signup-button click. Used by both
 * the signup flow (raw form email) and the in-app checkout (session email);
 * the server re-checks at init either way.
 */
export function checkSignupPlanApproval(
  email: string,
  tierCode: string,
): Promise<{ approved: boolean }> {
  return invokeSignupCheckout<{ approved: boolean }>({
    action: 'check_approval',
    email,
    tierCode,
  });
}

/**
 * True means this email is pre-entitled (allowlist / K&C import) — never show
 * a plan selector or charge them; their access self-provisions after they
 * confirm their email.
 */
export function checkSignupEntitlement(
  userId: string,
  email: string,
): Promise<{ entitled: boolean }> {
  return invokeSignupCheckout<{ entitled: boolean }>({ action: 'entitlement', userId, email });
}

/**
 * Create the subscription (incomplete) and return its first invoice's
 * PaymentIntent client_secret, which the frontend confirms on-session. Throws
 * with message === SIGNUP_CHECKOUT_UNAVAILABLE on a generic backend failure —
 * callers should fall through to the verify-email screen rather than surface
 * a dead-end error.
 */
export function initSignupCheckout(
  userId: string,
  email: string,
  selection: SignupCheckoutSelection,
  promoCode?: string,
): Promise<SignupInitResponse> {
  return invokeSignupCheckout<SignupInitResponse>({
    action: 'init',
    userId,
    email,
    selection,
    // Omit entirely when empty — older backends reject unknown falsy shapes and
    // "no promo" must stay byte-identical to the pre-promo request.
    ...(promoCode?.trim() ? { promoCode: promoCode.trim() } : {}),
  });
}

// ── Signup-time intake (free / access-request continuation) ───────────────────
//
// Backs the two non-payment continuations off the signup plan-select step:
// choosing Free, or requesting access to an application-gated K&C tier (K2K /
// Inner Circle). Both record a durable server-side intent for the just-created
// (still-unconfirmed) user via the public `signup-intake` edge function — same
// unauthenticated contract as stripe-checkout-signup above (userId + email
// travel in the body, no session). `request_access` both files the access
// request AND records a free-continuation intent, so the caller can always
// advance to verify-email exactly like the plain free path.

export type SignupIntakeAccessTier = 'K2K' | 'INNER_CIRCLE' | 'ENTERPRISE';

export interface SignupIntakeAccessRequestValues {
  fullName: string;
  businessName: string | null;
  /** The applicant's preferred contact email from the request-access form —
   * may differ from the account's identity email (e.g. a business inbox).
   * Sent as `contactEmail`; the backend stores contactEmail ?? email on the
   * access-request row. NEVER used for identity — see `accountEmail` below. */
  email: string;
  message: string | null;
}

export interface SignupIntakeResponse {
  ok: true;
  intent: 'free' | 'access_request';
  accessRequestId?: string;
  deduped?: boolean;
}

async function invokeSignupIntake(body: Record<string, unknown>): Promise<SignupIntakeResponse> {
  const { data, error } = await supabase.functions.invoke<SignupIntakeResponse>('signup-intake', {
    body,
  });
  if (error) {
    throw new Error(await edgeErrorMessage(error, 'Signup intake request failed'));
  }
  if (!data) throw new Error('Empty signup intake response');
  return data;
}

/**
 * Tells the server the person just confirmed their email, so a pending Free pick
 * reaches GoHighLevel now ('alfie free') instead of waiting for organisation
 * creation. Needs the fresh session (supabase.functions.invoke sends it). Best
 * effort: a failure is logged and never blocks the confirmation.
 */
export async function notifySignupConfirmed(userId: string, email: string): Promise<void> {
  const { error } = await supabase.functions.invoke('signup-intake', {
    body: { action: 'confirmed', userId, email },
  });
  if (error) console.error('signup-intake confirmed failed', error);
}

/** Records a durable "chose Free" intent for the just-created (unconfirmed) user. */
export function recordSignupFreeIntent(userId: string, email: string): Promise<SignupIntakeResponse> {
  return invokeSignupIntake({ action: 'select_free', userId, email });
}

/**
 * Files the access request for an application-gated K&C tier (K2K / Inner
 * Circle) and records the free-continuation intent, keyed to the just-created
 * (unconfirmed) user.
 *
 * `accountEmail` is the IDENTITY email — it must be the email the account was
 * actually signed up with (SignupPage's `email`/`formValues.email` state), NOT
 * the (editable) email typed into the request-access form. The backend's
 * unconfirmed-proof check matches it against auth.users and 401s on a
 * mismatch. `values.email` — the applicant's own contact email from the form,
 * which may legitimately differ — rides separately as `contactEmail`; the
 * backend stores contactEmail ?? email on the access-request row.
 */
export function submitSignupAccessRequest(
  userId: string,
  accountEmail: string,
  tierCode: SignupIntakeAccessTier,
  values: SignupIntakeAccessRequestValues,
): Promise<SignupIntakeResponse> {
  return invokeSignupIntake({
    action: 'request_access',
    userId,
    email: accountEmail,
    contactEmail: values.email,
    tierCode,
    fullName: values.fullName,
    businessName: values.businessName,
    message: values.message,
  });
}

// ── Billing tab (Settings → Billing, stripe-billing edge function) ────────────

export type ProductKind = 'alfie' | 'kc';

/** Live Stripe state for a subscription — the DB row lags the webhook. */
export interface StripeSubscriptionInfo {
  status: string;
  cancelAtPeriodEnd: boolean;
  /** When a cancel is pending, the date access ends. */
  cancelAt: string | null;
  currentPeriodEnd: string | null;
}

export interface BillingProductSubscription {
  kind: ProductKind;
  tierCode: string;
  tierName: string;
  status: string;
  billingInterval: string | null;
  amountCents: number | null;
  currentPeriodEnd: string | null;
  commitmentEndsAt: string | null;
  /** Null for a comp/included plan (no Stripe subscription behind it). */
  stripeSubscriptionId: string | null;
  /** True when this is a comp/included plan (no separate Stripe charge). */
  isComp: boolean;
  /** Null when comp, or when the live Stripe read failed — fall back to the DB fields. */
  stripe: StripeSubscriptionInfo | null;
}

export interface BillingPaymentMethod {
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
}

export interface BillingInvoice {
  id: string;
  createdAt: string;
  amountCents: number;
  currency: string;
  status: string | null;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
  description: string | null;
}

export interface BillingSummary {
  alfie: BillingProductSubscription | null;
  kc: BillingProductSubscription | null;
  paymentMethod: BillingPaymentMethod | null;
  invoices: BillingInvoice[];
}

async function invokeBilling<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke<T>('stripe-billing', {
    body,
  });
  if (error) {
    throw new Error(await edgeErrorMessage(error, 'Billing request failed'));
  }
  if (!data) throw new Error('Empty billing response');
  return data;
}

/** Both product subscriptions + payment method + recent invoices (owner/admin only). */
export function fetchBillingSummary(): Promise<BillingSummary> {
  return invokeBilling<BillingSummary>({ action: 'summary' });
}

/** Schedule a cancel at period end. */
export function cancelSubscription(
  kind: ProductKind,
): Promise<{ ok: boolean; subscription: StripeSubscriptionInfo }> {
  return invokeBilling({ action: 'cancel', kind });
}

/** Undo a pending cancel before the period ends. */
export function resumeSubscription(
  kind: ProductKind,
): Promise<{ ok: boolean; subscription: StripeSubscriptionInfo }> {
  return invokeBilling({ action: 'resume', kind });
}

// ── Card update (Settings → Billing, ClickUp 14zfy41wab8) ────────────────────

/** An open invoice the browser may pay on-session with the new card. */
export interface CardUpdateOpenInvoice {
  invoiceId: string;
  amountCents: number;
  currency: string;
  /** The invoice's PaymentIntent secret; null when it can't be paid from here. */
  paymentIntentClientSecret: string | null;
}

export interface CardSetupFinishResult {
  /** The new card's Stripe id, used to pay any open invoice. */
  paymentMethodId: string;
  paymentMethod: BillingPaymentMethod | null;
  subscriptionsUpdated: number;
  openInvoices: CardUpdateOpenInvoice[];
}

/** Start a card update: a SetupIntent secret for Stripe's card form. */
export function startCardSetup(): Promise<{ clientSecret: string; setupIntentId: string }> {
  return invokeBilling({ action: 'card_setup_start' });
}

/** After the bank confirmed the card: make it the default and list open invoices. */
export function finishCardSetup(setupIntentId: string): Promise<CardSetupFinishResult> {
  return invokeBilling({ action: 'card_setup_finish', setupIntentId });
}
